import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";

export interface ConferenceAttendance {
    id: number;
    registration_id: number;
    conference_id: number;
    room_id?: number | null;
    is_present: boolean;
    is_best_paper: boolean;
    best_paper_rank?: number | null;
    is_best_presenter: boolean;
    paper_score?: number | null;
    presenter_score?: number | null;
    notes?: string | null;
    created_at?: string;
    updated_at?: string;
    room?: {
        id: number;
        name: string;
        period_id?: number;
        date?: string;
    };
    registration?: {
        participant_name?: string;
        email?: string;
        institution_name?: string;
        submission_title?: string;
        submission_authors?: string;
        whatsapp_number?: string;
        is_presenter?: boolean;
        package?: {
            name: string;
        };
        certificates?: {
            id: number;
            certificate_type_id: number;
            is_file_generated: boolean;
        }[];
    };
}

export const useAdminRoomAttendances = (roomId?: string, conferenceId?: string) => {
    return useQuery({
        queryKey: ["admin-room-attendances", roomId, conferenceId],
        queryFn: async () => {
            if (!roomId && !conferenceId) return [];
            if (roomId && roomId !== "all") {
                const { data } = await axiosClient.get(`/private/conference-attendances/room/${roomId}`);
                return data.data as ConferenceAttendance[];
            }
            if (roomId === "all" && conferenceId && conferenceId !== "all") {
                const { data } = await axiosClient.get(`/private/conference-attendances/conference/${conferenceId}`);
                return data.data as ConferenceAttendance[];
            }
            return [];
        },
        enabled: (!!roomId && roomId !== "all") || (roomId === "all" && !!conferenceId && conferenceId !== "all"),
    });
};

export const useTogglePresence = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, is_present }: { id: number; is_present: boolean }) => {
            const { data } = await axiosClient.put(`/private/conference-attendances/${id}/presence`, { is_present });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-room-attendances"] });
            queryClient.invalidateQueries({ queryKey: ["admin-track-attendances"] });
        },
    });
};

export const useToggleBestPaper = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, best_paper_rank }: { id: number; best_paper_rank: number | null }) => {
            const { data } = await axiosClient.put(`/private/conference-attendances/${id}/best-paper`, { best_paper_rank });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-room-attendances"] });
            queryClient.invalidateQueries({ queryKey: ["admin-track-attendances"] });
        },
    });
};

export const useToggleBestPresenter = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, is_best_presenter }: { id: number; is_best_presenter: boolean }) => {
            const { data } = await axiosClient.put(`/private/conference-attendances/${id}/best-presenter`, { is_best_presenter });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-room-attendances"] });
            queryClient.invalidateQueries({ queryKey: ["admin-track-attendances"] });
        },
    });
};




export interface PlottingParticipant {
    registration_id: number;
    participant_name: string;
    institution: string;
    email: string;
    is_presenter: boolean;
    submission_title: string | null;
    submission_authors: string | null;
    package_name: string | null;
    room_id: number | null;
    room_name: string | null;
    status: string | null;
    ojs_submission_id: number | string | null;
}

export const useParticipantsForPlotting = (conferenceId?: number) => {
    return useQuery({
        queryKey: ["admin-participants-for-plotting", conferenceId],
        queryFn: async () => {
            if (!conferenceId) return [];
            const { data } = await axiosClient.get(`/private/conference-attendances/plot-participants?conference_id=${conferenceId}`);
            return data.data as PlottingParticipant[];
        },
        enabled: !!conferenceId,
    });
};

export const useAssignRoom = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ conference_id, registration_id, room_id }: { conference_id: number; registration_id: number; room_id: number }) => {
            const { data } = await axiosClient.post(`/private/conference-attendances/assign-room`, { conference_id, registration_id, room_id });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-participants-for-plotting"] });
            return queryClient.invalidateQueries({ queryKey: ["admin-room-attendances"] });
        },
    });
};

export const useUnassignRoom = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ registration_id }: { registration_id: number }) => {
            const { data } = await axiosClient.post(`/private/conference-attendances/unassign-room`, { registration_id });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-participants-for-plotting"] });
            return queryClient.invalidateQueries({ queryKey: ["admin-room-attendances"] });
        },
    });
};

export const useUpdateScores = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: { paper_score?: number | null; presenter_score?: number | null; notes?: string | null } }) => {
            const finalPayload: any = { ...payload };
            const { data } = await axiosClient.put(`/private/conference-attendances/${id}/scores`, finalPayload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-room-attendances"] });
            queryClient.invalidateQueries({ queryKey: ["admin-track-attendances"] });
        },
    });
};

export const useAutoRankBestPaper = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (conferenceId: number) => {
            const { data } = await axiosClient.post(`/private/conference-attendances/conference/${conferenceId}/auto-rank`);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-room-attendances"] });
            queryClient.invalidateQueries({ queryKey: ["admin-track-attendances"] });
        },
    });
};


export const useAdminTrackAttendances = (conferenceId?: string) => {
    return useQuery({
        queryKey: ["admin-track-attendances", conferenceId],
        queryFn: async () => {
            if (!conferenceId || conferenceId === "all") return [];
            const { data } = await axiosClient.get(`/private/conference-attendances/conference/${conferenceId}`);
            return data.data as ConferenceAttendance[];
        },
        enabled: !!conferenceId && conferenceId !== "all",
    });
};

export const exportConferenceAttendancesExcel = async (conferenceId: string, roomId: string, presence: string = 'all', status: string = 'all', achievement: string = 'all') => {
    let endpoint = `/private/conference-attendances/export/excel?conference_id=${conferenceId}&room_id=${roomId}`;
    if (presence !== 'all') endpoint += `&presence=${presence}`;
    if (status !== 'all') endpoint += `&status=${status}`;
    if (achievement !== 'all') endpoint += `&achievement=${achievement}`;
    
    const response = await axiosClient.get(endpoint, {
        responseType: 'blob',
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    const contentDisposition = response.headers['content-disposition'];
    let fileName = 'Data_Penilaian_Partisipan.xlsx';
    if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch.length === 2) {
            fileName = fileNameMatch[1];
        }
    }
    
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
};

export const useAutoRankBestPresenter = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (roomId: number) => {
            const { data } = await axiosClient.post(`/private/conference-attendances/room/${roomId}/auto-rank`);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-room-attendances"] });
            queryClient.invalidateQueries({ queryKey: ["admin-track-attendances"] });
        },
    });
};


